/**
 * Convert email block conditions to Formly hideExpression
 * Handles: email block
 */

export interface EmailConditionProps {
	mode: 'domain' | 'not-domain' | 'address' | 'not-address' | 'defined' | 'undefined';
	match?: string;
}

/**
 * Convert an email condition to hideExpression
 * Returns expression that evaluates to TRUE when condition is MET
 * (Remember: hideExpression hides when TRUE, so we'll invert later)
 *
 * Note: Email conditions always use lowercase comparison
 */
export function convertEmailCondition(fieldKey: string, props: EmailConditionProps): string | undefined {
	const { mode, match } = props;

	switch (mode) {
		case 'domain':
			// Email domain (after @) must match the specified domain
			if (match === undefined || match === null) {
				return undefined;
			}
			return `((model['${fieldKey}'] || '').toLowerCase().split('@')[1] === ${formatMatchValue(match)})`;

		case 'not-domain':
			// Email domain (after @) must NOT match the specified domain
			if (match === undefined || match === null) {
				return undefined;
			}
			return `((model['${fieldKey}'] || '').toLowerCase().split('@')[1] !== ${formatMatchValue(match)})`;

		case 'address':
			// Full email address must match the specified address
			if (match === undefined || match === null) {
				return undefined;
			}
			return `((model['${fieldKey}'] || '').toLowerCase() === ${formatMatchValue(match)})`;

		case 'not-address':
			// Full email address must NOT match the specified address
			if (match === undefined || match === null) {
				return undefined;
			}
			return `((model['${fieldKey}'] || '').toLowerCase() !== ${formatMatchValue(match)})`;

		case 'defined':
			// Email field must have a value (not empty string)
			return `!!(model['${fieldKey}'] && model['${fieldKey}'] !== '')`;

		case 'undefined':
			// Email field must be empty or undefined
			return `!(model['${fieldKey}'] && model['${fieldKey}'] !== '')`;

		default:
			// Unknown mode
			return undefined;
	}
}

/**
 * Format match value for comparison
 * Always converts to lowercase for email comparisons
 */
function formatMatchValue(match: string | undefined): string {
	if (match === undefined || match === null) {
		return "''";
	}

	// Escape single quotes in the string and convert to lowercase
	const escaped = match.replace(/'/g, "\\'").toLowerCase();
	return `'${escaped}'`;
}
